/**
 * „Berts Läufer" — eine Figur mit ihrem Besitzer (Befund **B6**, Zyklus R6).
 *
 * **Warum es das gibt.** Vor **R2** war das Opfer eines Stoßes oder Schlages immer gegnerisch;
 * „Stoßer stößt Wächter" sagte damit alles. Seit R2 stößt der Stoßer **jede** Figur, auch die
 * eigene und die verbündete — die Sichtprüfung zu R5 fand die Zeile „Anna: Stoßer stößt Stoßer
 * von yellow-A2 nach yellow-A3", in der offenblieb, wessen Figur gerade verschoben wurde. Ab V3
 * ist das der Unterschied zwischen einem auswertbaren Verlauf und Raten.
 *
 * **An zwei Stellen gebraucht, deshalb an einer geschrieben:** die Historie löst den Besitzer aus
 * dem Ereignis auf (`victimOwner`, weil eine geschlagene Figur nicht mehr auf dem Brett steht),
 * die Aktionsliste aus dem Brett (dort steht das Opfer noch). Die **Form** ist beide Male
 * dieselbe, und zwei Fassungen liefen beim nächsten Textwechsel auseinander.
 *
 * **Die Grammatik liegt in den Übersetzungen, die Auswahl im Code.** Ein `name + 's'` hier wäre
 * ein deutscher Genitiv an einer Stelle, die keine Sprache kennen darf — eine andere Übersetzung
 * ordnet anders („Bert's runner", „le coureur de Bert"). Welche der beiden Formen gilt, ist
 * dagegen eine Entscheidung über den Namen und keine über die Sprache des Satzes.
 */

/** Nur das, was hier gebraucht wird — so bleibt der Helfer ohne Abhängigkeit auf i18next. */
type Translate = (key: string, options: Record<string, string>) => string;

/**
 * Endet der Name auf einen Zischlaut, steht ein Apostroph statt des s.
 *
 * „Hans' Läufer", nicht „Hanss Läufer". Betrifft s, ß, x und z — und die Schreibweisen ce/ge
 * bleiben außen vor, weil sie im Deutschen uneinheitlich behandelt werden und ein Testspielername
 * sie praktisch nie trägt.
 */
const SIBILANT = /[sßxz]$/iu;

export function ownedPieceName(
  t: Translate,
  ownerName: string | undefined,
  pieceName: string,
): string {
  // **Ohne Besitzer bleibt die Figur allein stehen** — nicht „undefineds Läufer". Das ist der
  // Fall einer Partie aus der Zeit vor R6, deren Ereignis ihn nicht mitführt, und dieselbe
  // Vorsicht, mit der die Historie ihren ganzen Payload liest.
  if (ownerName === undefined || ownerName === '') {
    return pieceName;
  }
  const key = SIBILANT.test(ownerName) ? 'play:ownedPieceSibilant' : 'play:ownedPiece';
  return t(key, { owner: ownerName, piece: pieceName });
}
